import { For, Show } from "solid-js";
import { BRAND_TAB, SCAN_SECTION_KEY } from "../../enums/scan";
import { TOV_GUIDELINE_CATEGORY } from "../../enums/tov";
import { isAdmin } from "../../store/admin";
import { evidenceBySource } from "../tov/guidelineCoverage";
import { BrandGuidelinesSetupPanel } from "./BrandGuidelinesSetupPanel";
import { OverviewTab } from "./BrandOverviewPanel";
import { ActivityTab, GuidanceTab, SourcesTab } from "./brandGuidanceTabs";
import { ActiveReferenceLayer } from "./brandOverviewTab";
import { EvidenceDrawer } from "./EvidenceDrawer";
import { ScanRouteHeader } from "./ScanRouteHeader";
import { ScanShell } from "./ScanShell";
import { TABS, useBrandGuidelines } from "./useBrandGuidelines";

export function ScanBrandGuidelines(props: { websiteId: string }) {
  const {
    context,
    clientId,
    hasActiveVersion,
    tovVersionId,
    mapAttempt,
    activeTab,
    setActiveTab,
    selectedEvidence,
    setSelectedEvidence,
    activeProfile,
    version,
    sourceList,
    detailMap,
    trust,
    cards,
    counts,
    linkedEvidenceCount,
    unestablishedEvidenceCount,
    traceability,
    sourceCoverage,
    populatedCoverage,
    quietEmptyCoverage,
    inspectEvidence,
  } = useBrandGuidelines(props);

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.BRAND_GUIDELINES}>
      <div class="signal-route-surface signal-brand-guidelines">
        <ScanRouteHeader
          websiteId={props.websiteId}
          section="Brand Guidelines"
          title="Brand Guidelines"
          subtitle="Review the assessment context used where active guidance applies to Content Fitness subcriteria."
          actions={
            <Show
              when={isAdmin()}
              fallback={
                <span
                  class="btn btn-primary disabled"
                  title="Sign in to manage guidelines"
                >
                  Manage guidelines
                </span>
              }
            >
              <a
                class="btn btn-primary"
                href={`/w/${props.websiteId}/scan/brand-guidelines/manage`}
              >
                Manage guidelines
              </a>
            </Show>
          }
        />

        <section class="signal-panel signal-guideline-context">
          <span class="signal-mini-kicker">Assessment context</span>
          <h2>How your guidance is used</h2>
          <p>
            Faro applies relevant brand and content guidance when assessing each
            page. Findings link back to guidance and evidence where available.
          </p>
        </section>

        <Show
          when={context.data()}
          fallback={
            <div class="loading signal-loading">
              Loading Brand Guidelines...
            </div>
          }
        >
          <Show
            when={activeProfile()}
            keyed
            fallback={
              <section class="signal-panel signal-empty-state">
                <span class="signal-mini-kicker">Brand Guidelines</span>
                <h2>No active guideline profile</h2>
                <p>
                  No guideline context is available for assessment. Activate a
                  version in the guideline workspace to make guidance available.
                </p>
              </section>
            }
          >
            {(profile) => (
              <Show
                when={hasActiveVersion()}
                fallback={
                  <BrandGuidelinesSetupPanel
                    websiteId={props.websiteId}
                    clientId={clientId()}
                    versionId={tovVersionId()}
                    state={mapAttempt.state()}
                    summary={mapAttempt.summary()}
                    sourceCount={sourceList()?.length ?? null}
                  />
                }
              >
                <ActiveReferenceLayer
                  websiteId={props.websiteId}
                  profile={profile}
                  version={version() ?? undefined}
                  sources={sourceList() ?? undefined}
                  sourceCoverage={sourceCoverage()}
                  linkedEvidenceCount={linkedEvidenceCount()}
                  unestablishedEvidenceCount={unestablishedEvidenceCount()}
                  traceability={traceability()}
                  trust={trust()}
                  site={context.data()?.site ?? null}
                />

                <Show
                  when={version()}
                  keyed
                  fallback={
                    <section class="signal-panel signal-empty-state">
                      <span class="signal-mini-kicker">Guideline version</span>
                      <h2>Loading the active guideline version</h2>
                      <p>The active version's guidance is being loaded.</p>
                    </section>
                  }
                >
                  {(loaded) => (
                    <>
                      <nav
                        class="signal-guideline-tabs"
                        aria-label="Brand Guidelines sections"
                      >
                        <For each={TABS}>
                          {(tab) => (
                            <button
                              type="button"
                              class={activeTab() === tab.key ? "is-active" : ""}
                              aria-pressed={activeTab() === tab.key}
                              onClick={() => setActiveTab(tab.key)}
                            >
                              {tab.label}
                            </button>
                          )}
                        </For>
                      </nav>

                      <Show when={activeTab() === BRAND_TAB.OVERVIEW}>
                        <OverviewTab
                          counts={counts()}
                          populatedCoverage={populatedCoverage()}
                          quietEmptyCoverage={quietEmptyCoverage()}
                          cards={cards()}
                          sources={sourceList() ?? []}
                          sourceDetails={detailMap()}
                          sourceCoverage={sourceCoverage()}
                          traceability={traceability()}
                          trust={trust()}
                          onInspect={inspectEvidence(profile, loaded)}
                        />
                      </Show>
                      <Show when={activeTab() === BRAND_TAB.VOICE}>
                        <GuidanceTab
                          title="Voice & Tone"
                          description="Voice definitions, tone dimensions, rules, lexicon and examples when the active version contains them."
                          emptyTitle="No Voice & Tone guidance"
                          emptyText="This active version does not currently store traits, tone dimensions, rules, lexicon or examples."
                          cards={cards().filter(
                            (item) =>
                              item.category ===
                              TOV_GUIDELINE_CATEGORY.VOICE_TONE,
                          )}
                          onInspect={inspectEvidence(profile, loaded)}
                        />
                      </Show>
                      <Show when={activeTab() === BRAND_TAB.STRATEGY}>
                        <GuidanceTab
                          title="Strategy"
                          description="Pillars, audiences, journeys and visual guidance that exist in the active version."
                          emptyTitle="No strategy guidance"
                          emptyText="No strategy definitions are stored for this active version."
                          cards={cards().filter(
                            (item) =>
                              item.category === TOV_GUIDELINE_CATEGORY.STRATEGY,
                          )}
                          onInspect={inspectEvidence(profile, loaded)}
                        />
                      </Show>
                      <Show when={activeTab() === BRAND_TAB.CHANNELS}>
                        <GuidanceTab
                          title="Channels"
                          description="Channel guidance and supporting evidence from the active version."
                          emptyTitle="No channel guidance"
                          emptyText="No channel definitions are stored for this active version."
                          cards={cards().filter(
                            (item) =>
                              item.category === TOV_GUIDELINE_CATEGORY.CHANNELS,
                          )}
                          onInspect={inspectEvidence(profile, loaded)}
                        />
                      </Show>
                      <Show when={activeTab() === BRAND_TAB.SOURCES}>
                        <SourcesTab
                          sources={sourceList() ?? []}
                          details={detailMap()}
                          evidenceBySource={evidenceBySource(cards())}
                          trust={trust()}
                        />
                      </Show>
                      <Show when={activeTab() === BRAND_TAB.ACTIVITY}>
                        <ActivityTab events={loaded.revisionEvents} />
                      </Show>
                    </>
                  )}
                </Show>
              </Show>
            )}
          </Show>
        </Show>

        <Show when={selectedEvidence()} keyed>
          {(selection) => (
            <EvidenceDrawer
              selection={selection}
              websiteId={props.websiteId}
              onClose={() => setSelectedEvidence(null)}
            />
          )}
        </Show>
      </div>
    </ScanShell>
  );
}
